import { useCallback, useState } from 'react';

import type { ChatContext } from '../chat-context-types';

type ChatContextRegistry = {
    readonly registerChatContext: (tileId: string, context: ChatContext) => void;
    readonly unregisterChatContext: (tileId: string) => void;
    readonly activeChatContext: ChatContext | null;
};

/**
 * Shallow-equal so callers can pass an inline literal without re-allocating
 * the Map and re-rendering every consumer on every parent render.
 */
const isShallowEqualContext = (a: ChatContext, b: ChatContext): boolean => {
    const keysA = Object.keys(a) as ReadonlyArray<keyof ChatContext>;
    const keysB = Object.keys(b) as ReadonlyArray<keyof ChatContext>;

    if (keysA.length !== keysB.length) {
        return false;
    }

    return keysA.every((key) => a[key] === b[key]);
};

/**
 * Maintains a tile-id → ChatContext registry and exposes the context
 * associated with the given `activeTileId`. Re-registration with a
 * shallowly-equal context is a no-op (preserves referential stability).
 */
export const useChatContextRegistry = (activeTileId: string): ChatContextRegistry => {
    const [contextsByTileId, setContextsByTileId] = useState<ReadonlyMap<string, ChatContext>>(() => new Map());

    const registerChatContext = useCallback((tileId: string, context: ChatContext): void => {
        setContextsByTileId((previous) => {
            const existing = previous.get(tileId);

            if (existing !== undefined && isShallowEqualContext(existing, context)) {
                return previous;
            }

            const next = new Map(previous);
            next.set(tileId, context);

            return next;
        });
    }, []);

    const unregisterChatContext = useCallback((tileId: string): void => {
        setContextsByTileId((previous) => {
            if (!previous.has(tileId)) {
                return previous;
            }

            const next = new Map(previous);
            next.delete(tileId);

            return next;
        });
    }, []);

    const activeChatContext = contextsByTileId.get(activeTileId) ?? null;

    return { activeChatContext, registerChatContext, unregisterChatContext };
};
